const SOURCE_FILES = {
  BOOK: "frontend/public/learn/library.json#como-llegar-a-la-xima",
  RULES: "frontend/public/rules/es/xima-rules.json",
  KNOWLEDGE: "docs/xima-knowledge/reglas-generales.md; docs/xima-knowledge/sistema-de-movimientos.md",
  CIMAX: "backend/utils/cimaxScoring.js; frontend/src/pages/user/Stats.js; backend/models/MatchHistory.js",
  UNITY: "Xima_Game/Assets/Scripts/Core/Stage.cs; Xima_Game/Assets/Scripts/Core/Cube.cs; Xima_Game/Assets/Scripts/Player.cs",
};

const CATEGORY_META = {
  reglasAvanzadas: {
    label: "Reglas avanzadas",
    path: "reglas",
    headings: ["Que significa", "Como se produce", "Relacion con otras reglas"],
  },
  tableroLibro: {
    label: "Tablero avanzado",
    path: "reglas/tablero-y-casillas",
    headings: ["Concepto del tablero", "Como leerlo", "Por que importa"],
  },
  notacionLibro: {
    label: "Notacion avanzada",
    path: "sistema-de-anotacion",
    headings: ["Sistema descrito", "Como interpretarlo", "Uso para estudiar"],
  },
  estrategiaLibro: {
    label: "Estrategia del libro",
    path: "academia",
    headings: ["Idea estrategica", "Como practicarla", "Idea clave"],
  },
  cimaxLibro: {
    label: "CIMAX del libro",
    path: "estadisticas/cimax",
    headings: ["Marco del libro", "Relacion con el juego", "Como interpretarlo"],
  },
};

const link = (label, path) => ({ label, path });
const faq = (question, answer) => ({ question, answer });
const refs = (codes) => (codes || []).map((code) => SOURCE_FILES[code] || code);

const buildLinks = (route, links) =>
  (links || []).map((item) => ({
    label: item.label,
    path: route(item.path),
  }));

const buildCta = (route, spec, category) => {
  const cta = spec.cta || { label: `Ver ${category.label.toLowerCase()}`, path: category.path };
  return {
    label: cta.label,
    path: route(cta.path),
  };
};

const buildSections = (spec, category) => {
  const headings = spec.headings || category.headings;
  const sections = [
    ...(spec.sections || [
      { heading: headings[0], body: spec.verified },
      { heading: headings[1], body: spec.usage },
      { heading: headings[2], body: spec.caution },
    ]),
  ];

  if (spec.steps?.length > 0) {
    sections.push({
      heading: "Como analizarlo",
      body: spec.steps.slice(0, 3).join(" "),
    });
  }

  if (spec.examples?.length > 0) {
    sections.push({
      heading: "Ejemplo practico",
      body: spec.examples[0],
    });
  }

  return sections
    .filter((section) => String(section?.heading || "").trim() && String(section?.body || "").trim())
    .slice(0, 6);
};

const hasMinimumPublicInformation = (spec) => {
  const category = CATEGORY_META[spec.category] || CATEGORY_META.estrategiaLibro;
  return buildSections(spec, category).length >= 5;
};

const buildBreadcrumbs = (route, spec, category) => [
  { label: category.label, path: route(category.path) },
  { label: spec.h1, path: route(spec.slug) },
];

const defaultScreenshots = (spec) => [
  `Referencia visual del libro: ${spec.h1}`,
  `Diagrama de apoyo para estudiar ${spec.h1}`,
];

const buildLearnPage = ({ page, route, canonical }, spec) => {
  const category = CATEGORY_META[spec.category] || CATEGORY_META.estrategiaLibro;
  const status = spec.status || "verified";
  const steps = spec.steps || [];

  return page({
    slug: spec.slug,
    friendlyUrl: canonical(spec.slug),
    title: spec.title,
    metaDescription: spec.metaDescription,
    h1: spec.h1,
    intro: spec.intro,
    sections: buildSections(spec, category),
    stepsTitle: spec.stepsTitle,
    stepsNote: spec.stepsNote,
    steps,
    examples: spec.examples || [],
    screenshots: spec.screenshots || defaultScreenshots(spec),
    faqs: spec.faqs || [],
    internalLinks: buildLinks(route, spec.links),
    cta: buildCta(route, spec, category),
    breadcrumbs: buildBreadcrumbs(route, spec, category),
    sources: refs(spec.sources),
    status,
    robots: spec.robots || (status === "ownerConfirmation" ? "noindex,follow" : "index,follow"),
    structuredDataType: spec.structuredDataType || (steps.length > 0 ? "HowTo" : "Article"),
  });
};

const BOOK_LEARN_TOPICS = [
  {
    slug: "reglas/ahogado",
    category: "reglasAvanzadas",
    title: "Ahogado en XIMA | Regla avanzada desde el libro",
    metaDescription:
      "Aprende que significa quedar ahogado en XIMA, como se relaciona con los bloques dominados y por que necesita lectura cuidadosa.",
    h1: "Ahogado en XIMA",
    intro:
      "El libro describe el ahogado como una situacion donde un jugador no puede mover ningun bloque en su turno y aun asi gana.",
    verified:
      "La regla de XIMA relacionada indica que si un jugador tiene todos sus bloques dominados en su turno, gana la partida. El libro lo presenta como una defensa extrema llamada ahogado.",
    usage:
      "Lee el ahogado revisando primero si el jugador tiene turno, si sus bloques estan en el tablero y si todos quedan sin posibilidad real de movimiento por dominacion.",
    caution:
      "El ahogado se diferencia de un bloqueo comun porque afecta al jugador en su turno completo: sus bloques no pueden actuar y la regla lo convierte en una victoria defensiva.",
    steps: [
      "Identifica al jugador que tiene el turno.",
      "Cuenta sus bloques disponibles dentro y fuera del tablero.",
      "Comprueba si los bloques en el tablero estan dominados o sin movimiento legal.",
      "Distingue ahogado de falta total de recursos, porque el resultado no es el mismo.",
      "Relaciona el resultado con la regla de dominacion completa antes de declarar la victoria.",
    ],
    examples: [
      "Si todos los bloques propios estan dominados en el turno del jugador, la regla de XIMA lo conecta con victoria.",
      "Si el jugador no tiene bloques dentro ni fuera, el caso se acerca a sin recurso, no al ahogado.",
    ],
    faqs: [
      faq("Ahogado es derrota?", "Segun el libro y la regla relacionada, el ahogado se trata como victoria defensiva."),
      faq("Todo bloqueo es ahogado?", "No. Debe revisarse el estado completo de los bloques y el turno."),
    ],
    links: [
      link("Reglas de XIMA", "reglas"),
      link("Dominacion de bloques", "reglas/dominacion-tipos"),
      link("Sin recurso", "reglas/sin-recurso"),
    ],
    cta: { label: "Ver dominacion avanzada", path: "reglas/dominacion-tipos" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "partial",
  },
  {
    slug: "reglas/sin-recurso",
    category: "reglasAvanzadas",
    title: "Sin recurso en XIMA | Perdida por falta de bloques",
    metaDescription:
      "Conoce la regla sin recurso en XIMA y como se diferencia del ahogado, la dominacion y el estancamiento.",
    h1: "Sin recurso",
    intro:
      "Sin recurso es el caso descrito en el libro donde un jugador no puede realizar ninguna funcion por falta de bloques dentro o fuera del tablero.",
    verified:
      "Las reglas de XIMA indican que un jugador pierde si en su turno no tiene bloques dentro ni fuera del tablero. El libro lo resume como carencia total de recursos.",
    usage:
      "Usalo para explicar una derrota por ausencia de material jugable, no para posiciones donde los bloques existen pero estan dominados.",
    caution:
      "La diferencia con ahogado esta en el material: en sin recurso el jugador queda sin bloques utiles dentro o fuera del tablero; en ahogado todavia puede tener bloques, pero no movilidad efectiva.",
    steps: [
      "Revisa si el jugador conserva bloques fuera del tablero.",
      "Revisa si tiene bloques propios dentro del tablero.",
      "Comprueba que no exista bloque especial disponible para rescate o reincorporacion.",
      "Distingue falta de material de una posicion dominada.",
      "Declara sin recurso solo si no queda ninguna funcion posible segun la regla.",
    ],
    examples: [
      "Un jugador sin bloques en tablero ni fuera queda en una condicion de perdida.",
      "Un jugador con bloques dominados se analiza primero como posible ahogado, porque todavia conserva material en el tablero.",
    ],
    faqs: [
      faq("Sin recurso y ahogado son iguales?", "No. Ahogado se relaciona con no poder mover; sin recurso con no tener bloques utiles disponibles."),
      faq("Puede haber rescate en sin recurso?", "El rescate depende de conservar un bloque comun capaz de llegar a la cima; sin material disponible, el jugador queda sin esa via."),
    ],
    links: [
      link("Ahogado en XIMA", "reglas/ahogado"),
      link("Rescate avanzado", "reglas/rescate-avanzado"),
      link("Reglas generales", "reglas"),
    ],
    cta: { label: "Comparar con ahogado", path: "reglas/ahogado" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "reglas/posicion-muerta",
    category: "reglasAvanzadas",
    title: "Posicion muerta en XIMA | Estancamiento hacia G7",
    metaDescription:
      "Explicacion prudente de posicion muerta en XIMA: cuando ningun jugador puede llegar a G7 con jugadas legales.",
    h1: "Posicion muerta",
    intro:
      "El libro llama posicion muerta a un estado donde ningun jugador puede llegar a G7 con el bloque especial mediante jugadas legales.",
    verified:
      "El libro conecta esta condicion con el incremento de material: si la partida queda cerrada hacia G7, se agregan recursos para abrir nuevas rutas.",
    usage:
      "Sirve para explicar por que XIMA evita tablas: cuando la ruta a la cima queda cerrada para todos, el sistema puede pasar a material extra.",
    caution:
      "Una posicion dificil todavia puede tener caminos largos hacia G7; la posicion muerta describe un cierre mas profundo, donde la cima deja de ser alcanzable por las rutas legales disponibles.",
    steps: [
      "Ubica G7 y los bloques especiales activos o rescatables.",
      "Revisa si existe alguna serie legal que permita llegar a la cima.",
      "Comprueba si la posicion fue producida por una jugada legal.",
      "Distingue bloqueo temporal de imposibilidad real.",
      "Relaciona el caso con incremento de material solo si corresponde.",
    ],
    examples: [
      "Una posicion cerrada donde ningun especial puede alcanzar G7 puede requerir incremento.",
      "Una ruta larga pero posible sigue siendo juego activo; la posicion muerta se refiere a cierre real de los caminos hacia la cima.",
    ],
    faqs: [
      faq("Posicion muerta es empate?", "No. El libro la relaciona con incremento de material porque XIMA evita tablas."),
      faq("Que se estudia en una posicion muerta?", "Se estudia si los especiales aun tienen una ruta legal hacia G7 o si el tablero quedo cerrado para todos."),
    ],
    links: [
      link("Incremento avanzado", "reglas/incremento-material-avanzado"),
      link("La cima", "reglas/cima"),
      link("Bloqueo y obstruccion", "reglas/bloqueo-vs-obstruccion"),
    ],
    cta: { label: "Ver incremento de material", path: "reglas/incremento-material-avanzado" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "partial",
  },
  {
    slug: "reglas/incremento-material-avanzado",
    category: "reglasAvanzadas",
    title: "Incremento de material en XIMA | Regla avanzada",
    metaDescription:
      "Aprende como el libro explica el incremento de material en XIMA y su relacion con estancamiento, repeticion y tres bloques extra.",
    h1: "Incremento de material avanzado",
    intro:
      "El incremento de material es la respuesta descrita para evitar tablas: cada jugador recibe tres bloques comunes adicionales.",
    verified:
      "Las reglas de XIMA indican que el incremento de material agrega tres bloques comunes extra para cada jugador ante empate o estancamiento.",
    usage:
      "El libro lo vincula con posicion muerta, acuerdo, repeticion de posiciones y una secuencia prolongada sin capturas ni ascensos.",
    caution:
      "El incremento busca devolver movimiento a una partida bloqueada: al entrar tres bloques comunes extra para todos, aparecen nuevas columnas, apoyos y rutas hacia G7.",
    steps: [
      "Detecta si la partida entro en estancamiento real.",
      "Revisa si la posicion se repite o bloquea la ruta a G7.",
      "Comprueba si las reglas de la sala permiten solicitar incremento.",
      "Agrega el mismo material para todos los jugadores cuando proceda.",
      "Reanuda la partida buscando nuevas rutas hacia la cima.",
    ],
    examples: [
      "Si la partida no puede progresar hacia G7, el incremento crea nuevas opciones.",
      "El incremento es simetrico: cada jugador recibe el mismo material extra para que la partida vuelva a resolverse sobre el tablero.",
    ],
    faqs: [
      faq("Cuantos bloques agrega?", "Tres bloques comunes extra para cada jugador."),
      faq("XIMA permite tablas?", "El libro y las reglas orientan la partida hacia incremento, no hacia tablas."),
    ],
    links: [
      link("Posicion muerta", "reglas/posicion-muerta"),
      link("Reglas de XIMA", "reglas"),
      link("Turnos en XIMA", "reglas/turnos"),
    ],
    cta: { label: "Ver posicion muerta", path: "reglas/posicion-muerta" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "partial",
  },
  {
    slug: "reglas/dominacion-tipos",
    category: "reglasAvanzadas",
    title: "Tipos de dominacion en XIMA | Propia, doble y mixta",
    metaDescription:
      "Guia avanzada de dominacion en XIMA: propia, simple, doble, triple, compuesta, mixta y multiple segun el libro.",
    h1: "Tipos de dominacion",
    intro:
      "Dominacion es el control que ejerce un bloque sobre otro en una columna, impidiendo movimiento y preparando posibles capturas.",
    verified:
      "La regla de dominacion indica que un bloque que mueve sobre otros impide mover a los inferiores y elimina la minoria contraria en esa columna.",
    usage:
      "El libro amplia la lectura con dominacion propia, simple, doble, triple, compuesta, mixta y multiple.",
    caution:
      "La dominacion cambia la movilidad de una columna: el bloque superior controla lo que queda debajo y puede transformar una posicion pasiva en una amenaza de captura.",
    steps: [
      "Identifica la columna donde hay bloques apilados.",
      "Determina que bloque queda arriba y que bloques quedan dominados.",
      "Cuenta si la dominacion afecta a uno, varios o distintos colores.",
      "Distingue control propio de control sobre rival.",
      "Evalua si la minoria contraria debe capturarse segun la regla.",
    ],
    examples: [
      "Una columna con dos bloques propios puede describirse como dominacion propia.",
      "Una columna que afecta varios bloques o colores puede requerir lectura compuesta o mixta.",
    ],
    faqs: [
      faq("Dominacion siempre captura?", "No necesariamente; la captura depende de la minoria contraria en la columna."),
      faq("Por que separar tipos?", "Ayuda a estudiar posiciones y explicar el control de columnas con mas precision."),
    ],
    links: [
      link("Capturas por tipo", "reglas/capturas-tipos"),
      link("Bloques comunes", "reglas/bloques"),
      link("Movimiento valido", "reglas/jugada-legal"),
    ],
    cta: { label: "Ver capturas por tipo", path: "reglas/capturas-tipos" },
    sources: ["BOOK", "RULES", "UNITY", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "reglas/capturas-tipos",
    category: "reglasAvanzadas",
    title: "Tipos de captura en XIMA | Sandwich y aplastamiento",
    metaDescription:
      "Aprende los tipos de captura que describe el libro de XIMA: sandwich, aplastamiento y captura mixta.",
    h1: "Tipos de captura",
    intro:
      "El libro diferencia capturas por sandwich, aplastamiento y captura mixta cuando la dominacion elimina la minoria de una columna.",
    verified:
      "La captura se basa en la columna: al dominar, se elimina la minoria que sea diferente al color dominante.",
    usage:
      "Usa estos nombres para analizar como se produjo la captura y que bloque creo la dominacion decisiva.",
    caution:
      "Los tipos de captura ayudan a reconstruir la jugada: muestran si la columna quedo decidida por presion entre bloques, por una nueva dominacion desde arriba o por mezcla de ambos patrones.",
    steps: [
      "Localiza la columna donde se produjo la dominacion.",
      "Identifica los colores presentes antes de la captura.",
      "Determina si la captura se produjo por volver a dominar la columna o por una nueva presion desde arriba.",
      "Clasifica el caso como sandwich, aplastamiento o mixta solo si el patron coincide.",
      "Comprueba si el especial capturado activa posibilidad de rescate.",
    ],
    examples: [
      "Una captura tipo sandwich aparece cuando una columna vuelve a quedar dominada por bloques del mismo color.",
      "Si el especial es capturado, la lectura debe continuar con la regla de rescate.",
    ],
    faqs: [
      faq("La captura elimina al especial para siempre?", "No. El especial capturado puede rescatarse mediante la regla de rescate."),
      faq("Los nombres de captura cambian la regla?", "No. Nombran patrones de una misma logica de columna."),
    ],
    links: [
      link("Dominacion avanzada", "reglas/dominacion-tipos"),
      link("Rescate avanzado", "reglas/rescate-avanzado"),
      link("Bloque especial", "reglas/bloque-especial"),
    ],
    cta: { label: "Ver rescate avanzado", path: "reglas/rescate-avanzado" },
    sources: ["BOOK", "RULES", "UNITY", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "reglas/oposicion-especial",
    category: "reglasAvanzadas",
    title: "Oposicion del especial en XIMA | Concepto del libro",
    metaDescription:
      "Conoce la oposicion del bloque especial en XIMA, una idea avanzada del libro para finales y defensa cerca de la cima.",
    h1: "Oposicion del especial",
    intro:
      "La oposicion ocurre cuando los bloques especiales quedan cerca, separados por linea o vertice, creando tension directa en el final.",
    verified:
      "La regla indica que un especial no puede subir ni pasar sobre otro especial. El libro desarrolla esa restriccion como oposicion.",
    usage:
      "En finales, la oposicion ayuda a entender cuando el especial funciona como bloque ofensivo, defensivo o punto de bloqueo.",
    caution:
      "La oposicion obliga a calcular apoyo: un especial cerca del rival no puede tratarse como un bloque comun, porque su restriccion frente a otro especial cambia las rutas hacia G7.",
    steps: [
      "Ubica ambos bloques especiales en el tablero.",
      "Comprueba si estan separados por linea o vertice cercano.",
      "Revisa si alguno intenta pasar sobre el otro o coexistir en la misma columna.",
      "Analiza que rutas hacia G7 quedan cerradas o abiertas.",
      "Usa bloques comunes para apoyar al especial sin violar la restriccion.",
    ],
    examples: [
      "Dos especiales enfrentados cerca de G7 pueden bloquearse tacticamente.",
      "Un bloque comun puede apoyar al especial buscando un angulo que no dependa de pasar sobre el especial contrario.",
    ],
    faqs: [
      faq("Oposicion es una regla nueva?", "No. Es una lectura estrategica de la restriccion entre bloques especiales."),
      faq("Importa mas en finales?", "El libro indica que toma maxima importancia durante el final."),
    ],
    links: [
      link("Finales en XIMA", "estrategia/finales-xima"),
      link("Bloque especial", "reglas/bloque-especial"),
      link("La cima", "reglas/cima"),
    ],
    cta: { label: "Ver finales en XIMA", path: "estrategia/finales-xima" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "partial",
  },
  {
    slug: "reglas/amenazas-al-especial",
    category: "reglasAvanzadas",
    title: "Amenazas al especial en XIMA | Como reconocerlas",
    metaDescription:
      "Guia avanzada para reconocer amenazas al bloque especial en XIMA y revisar evasiones, bloqueos y contraataques.",
    h1: "Amenazas al especial",
    intro:
      "El libro dedica una seccion amplia a las amenazas sobre el bloque especial y a formas de evadirlas durante la partida.",
    verified:
      "Las reglas dan importancia central al especial, la captura y el rescate. El libro amplia esa base con patrones de defensa para proteger la pieza que puede ganar.",
    usage:
      "Antes de mover el especial, revisa ataques por dominacion, bloqueo, obstruccion, recaptura y amenazas sobre G7.",
    caution:
      "Una amenaza al especial se puede responder moviendolo, dominando el bloque atacante, obstruyendo la linea, recapturando, desviando la ofensiva o creando una amenaza de cima.",
    steps: [
      "Ubica tu bloque especial y las columnas que pueden dominarlo.",
      "Busca bloques rivales alineados en horizontal, vertical o diagonal.",
      "Comprueba si puedes mover el especial a una casilla no atacada.",
      "Evalua bloquear, obstruir, recapturar o contraatacar el especial rival.",
      "Elige la defensa que mantenga una ruta futura hacia G7.",
    ],
    examples: [
      "Mover el especial a una casilla no atacada puede resolver una amenaza directa.",
      "Obstruir un bloque atacante puede ganar tiempo si tambien mantiene coordinacion propia.",
    ],
    faqs: [
      faq("Siempre debo mover el especial si esta amenazado?", "No. El libro tambien contempla bloqueo, obstruccion, recaptura y contraataque."),
      faq("Amenazar cima con un comun gana?", "No. Un comun solo se relaciona con G7 en contexto de rescate del especial capturado."),
    ],
    links: [
      link("Oposicion del especial", "reglas/oposicion-especial"),
      link("Obstruccion", "reglas/obstruccion"),
      link("Capturas por tipo", "reglas/capturas-tipos"),
    ],
    cta: { label: "Ver oposicion del especial", path: "reglas/oposicion-especial" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "partial",
  },
  {
    slug: "reglas/bloqueo-superior-inferior",
    category: "reglasAvanzadas",
    title: "Bloqueo superior e inferior en XIMA | Altura y niveles",
    metaDescription:
      "Aprende la diferencia entre bloqueo superior e inferior en XIMA y como afecta subir o bajar entre pisos.",
    h1: "Bloqueo superior e inferior",
    intro:
      "El libro define bloqueo como una estructura que impide ascenso o descenso entre pisos o niveles del tablero.",
    verified:
      "Las reglas permiten subir o bajar con restricciones de altura. El bloqueo explica cuando una estructura impide esa ruta.",
    usage:
      "Distingue si el obstaculo esta por encima del bloque que intenta subir o por debajo del bloque que intenta bajar.",
    caution:
      "El bloqueo se reconoce por la diferencia de niveles: el obstaculo impide que un bloque suba o baje aunque el objetivo parezca cercano.",
    steps: [
      "Identifica el bloque que quiere subir o bajar.",
      "Mira si la estructura que impide el paso esta en otro nivel.",
      "Clasifica como bloqueo superior si impide el ascenso desde arriba.",
      "Clasifica como bloqueo inferior si impide el descenso desde abajo.",
      "Busca una ruta alternativa o una jugada que modifique la estructura.",
    ],
    examples: [
      "Un bloque rival puede impedir que el especial alcance un piso superior.",
      "Una estructura inferior puede hacer ilegal una bajada hacia una captura.",
    ],
    faqs: [
      faq("Bloqueo y obstruccion son lo mismo?", "No. Bloqueo se relaciona con pisos distintos; obstruccion con desplazamiento en el mismo piso."),
      faq("Puede bloquearse al especial?", "Si, siempre que la posicion respete las reglas de movimiento y altura."),
    ],
    links: [
      link("Obstruccion", "reglas/obstruccion"),
      link("Bloqueo vs obstruccion", "reglas/bloqueo-vs-obstruccion"),
      link("Subir bloques", "reglas/subir-bloques"),
    ],
    cta: { label: "Comparar con obstruccion", path: "reglas/bloqueo-vs-obstruccion" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "verified",
  },
  {
    slug: "reglas/obstruccion",
    category: "reglasAvanzadas",
    title: "Obstruccion en XIMA | Total, parcial, interna y externa",
    metaDescription:
      "Conoce la obstruccion en XIMA segun el libro: cuando una estructura impide el desplazamiento en el mismo piso.",
    h1: "Obstruccion",
    intro:
      "Obstruccion es una estructura de bloques que impide el desplazamiento de otro bloque dentro del mismo piso del tablero.",
    verified:
      "El libro separa obstruccion total, parcial, interna y externa. El movimiento en linea y los limites del tablero explican por que una ruta queda cerrada.",
    usage:
      "Usala para explicar rutas cortadas en un mismo nivel: no es necesariamente captura, sino control del camino.",
    caution:
      "La obstruccion cambia el costo del camino: si es total, corta la ruta inmediata; si es parcial, obliga a rodear o gastar mas turnos.",
    steps: [
      "Ubica el bloque que quiere desplazarse dentro de un mismo piso.",
      "Traza la linea de movimiento que intenta usar.",
      "Identifica la estructura que corta esa linea.",
      "Determina si hay ruta alternativa inmediata o mas lenta.",
      "Clasifica la obstruccion como total o parcial segun las opciones restantes.",
    ],
    examples: [
      "Una obstruccion total impide llegar al destino sin rodear de forma significativa.",
      "Una obstruccion parcial retrasa el plan pero no lo elimina por completo.",
    ],
    faqs: [
      faq("Obstruccion captura bloques?", "No por si sola. Puede preparar presion, pero captura depende de dominacion."),
      faq("Que significa interna o externa?", "El libro lo relaciona con anillos interiores o exteriores del tablero."),
    ],
    links: [
      link("Bloqueo superior e inferior", "reglas/bloqueo-superior-inferior"),
      link("Pisos y anillos", "tablero/pisos-anillos-bandas"),
      link("Movimiento legal", "reglas/jugada-legal"),
    ],
    cta: { label: "Ver pisos y anillos", path: "tablero/pisos-anillos-bandas" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "verified",
  },
  {
    slug: "reglas/bloqueo-vs-obstruccion",
    category: "reglasAvanzadas",
    title: "Bloqueo vs obstruccion en XIMA | Diferencia practica",
    metaDescription:
      "Compara bloqueo y obstruccion en XIMA para entender si una estructura impide subir, bajar o desplazarse en el mismo piso.",
    h1: "Bloqueo vs obstruccion",
    intro:
      "El libro separa dos ideas cercanas: bloqueo para ascenso o descenso entre niveles, y obstruccion para desplazamiento en el mismo piso.",
    verified:
      "Ambos conceptos se apoyan en reglas de linea recta, altura y limites de movimiento.",
    usage:
      "La comparacion ayuda a revisar jugadas rechazadas: algunas fallan por altura y otras por camino ocupado o cerrado.",
    caution:
      "La diferencia practica esta en la causa del impedimento: altura y niveles para bloqueo, camino cerrado en el mismo piso para obstruccion.",
    steps: [
      "Determina si el bloque intenta cambiar de piso o mantenerse en el mismo nivel.",
      "Si cambia de piso, revisa bloqueo superior o inferior.",
      "Si se mantiene en el mismo piso, revisa obstruccion total o parcial.",
      "Comprueba si la ruta sigue siendo linea recta.",
      "Elige el termino que explique exactamente por que la jugada no progresa.",
    ],
    examples: [
      "Un ascenso negado por una estructura de altura corresponde a bloqueo.",
      "Una linea horizontal cerrada en el mismo piso corresponde a obstruccion.",
    ],
    faqs: [
      faq("Sirve para resolver errores de movimiento?", "Si. Ayuda a saber si el problema fue altura o camino."),
      faq("Puede haber bloqueo y obstruccion a la vez?", "En posiciones complejas puede haber varios impedimentos, pero se deben explicar por separado."),
    ],
    links: [
      link("Bloqueo superior e inferior", "reglas/bloqueo-superior-inferior"),
      link("Obstruccion", "reglas/obstruccion"),
      link("Movimiento no permitido", "solucion/movimiento-no-permitido"),
    ],
    cta: { label: "Ver movimiento no permitido", path: "solucion/movimiento-no-permitido" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "verified",
  },
  {
    slug: "reglas/rescate-avanzado",
    category: "reglasAvanzadas",
    title: "Rescate avanzado del especial en XIMA | G7 y reincorporacion",
    metaDescription:
      "Aprende la lectura avanzada del rescate del bloque especial en XIMA: comun a G7, intercambio y reincorporacion.",
    h1: "Rescate avanzado del especial",
    intro:
      "El libro explica que un bloque comun puede llegar a G7 solo cuando el especial propio fue capturado, para rescatarlo.",
    verified:
      "La regla de rescate permite que un bloque comun llegue a la cima para recuperar al especial, que luego podra incorporarse de nuevo.",
    usage:
      "El rescate no es una victoria normal del comun. Es un mecanismo para recuperar el especial y seguir compitiendo por la cima.",
    caution:
      "El rescate avanzado conecta tres ideas: el especial capturado, el bloque comun que alcanza G7 y la reincorporacion posterior del especial.",
    steps: [
      "Confirma que el bloque especial propio fue capturado.",
      "Busca un bloque comun propio capaz de llegar legalmente a G7.",
      "Comprueba que la llegada a G7 ocurre como rescate, no como victoria.",
      "Interpreta el intercambio como recuperacion del especial.",
      "Reincorpora el especial cuando la regla o flujo de la partida lo permita.",
    ],
    examples: [
      "Un comun que llega a G7 cumple funcion de rescate cuando el especial propio fue capturado.",
      "Despues del rescate, el especial vuelve a ser el bloque que puede ganar llegando a la cima.",
    ],
    faqs: [
      faq("El comun gana al llegar a G7?", "No como condicion normal de victoria; su llegada a G7 pertenece al rescate del especial capturado."),
      faq("El especial vuelve al tablero automaticamente?", "Despues del rescate, el especial puede incorporarse nuevamente cuando se desee."),
    ],
    links: [
      link("Bloque comun en G7", "reglas/bloque-comun-g7"),
      link("Bloque especial", "reglas/bloque-especial"),
      link("Rescate de bloques", "reglas/rescate"),
    ],
    cta: { label: "Ver bloque comun en G7", path: "reglas/bloque-comun-g7" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "reglas/bloque-comun-g7",
    category: "reglasAvanzadas",
    title: "Bloque comun y G7 en XIMA | Pasar no es ocupar",
    metaDescription:
      "Conoce la regla fina del bloque comun en G7: puede pasar por la casilla, pero solo llega para rescatar al especial.",
    h1: "Bloque comun y G7",
    intro:
      "El libro separa dos acciones: pasar por G7 durante un movimiento y llegar a G7 para rescate.",
    verified:
      "Un bloque comun no puede llegar a la cima salvo para rescatar al especial, aunque si puede pasar por G7.",
    usage:
      "Esta guia evita una confusion frecuente: tocar o cruzar la casilla no equivale a cumplir el objetivo de victoria.",
    caution:
      "La diferencia depende del destino: cruzar G7 durante una ruta no equivale a terminar en G7 para rescatar al especial.",
    steps: [
      "Observa si el bloque comun esta usando G7 como paso intermedio.",
      "Comprueba si el movimiento termina en G7 o solo cruza la casilla.",
      "Si termina en G7, verifica que el especial propio este capturado.",
      "Si no hay rescate, no presentes la llegada del comun como victoria.",
      "Vuelve a evaluar la ruta del especial para ganar la partida.",
    ],
    examples: [
      "Un comun puede cruzar G7 si el movimiento legal continua hacia otra casilla.",
      "Un comun solo puede ocupar G7 para rescatar al especial capturado.",
    ],
    faqs: [
      faq("G7 siempre gana?", "Gana el especial propio llegando legalmente; el comun tiene una excepcion de rescate."),
      faq("Por que importa la casilla final?", "Porque la regla distingue pasar por G7 de llegar a G7."),
    ],
    links: [
      link("Rescate avanzado", "reglas/rescate-avanzado"),
      link("La cima", "reglas/cima"),
      link("Sistema de movimientos", "reglas/movimientos"),
    ],
    cta: { label: "Ver rescate avanzado", path: "reglas/rescate-avanzado" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "tablero/pisos-anillos-bandas",
    category: "tableroLibro",
    title: "Pisos, anillos y bandas del tablero XIMA | Guia del libro",
    metaDescription:
      "Aprende como el libro describe los 4 pisos del tablero XIMA, sus anillos, bandas y lectura superior.",
    h1: "Pisos, anillos y bandas",
    intro:
      "El libro describe el tablero como una piramide escalonada de cuatro pisos, con anillos y bandas que ayudan a leer rutas.",
    verified:
      "La plataforma documenta tablero, casillas y G7. El libro agrega una lectura mas fina: vista superior, anillos internos y externos, bandas y pisos.",
    usage:
      "Usa esta lectura para entender por que ciertas rutas de ascenso, defensa o obstruccion dependen del anillo y la cara del tablero.",
    caution:
      "Los anillos y bandas ayudan a entender por que una casilla controla una ruta y otra, aunque parezca cercana, pertenece a una zona distinta del tablero.",
    steps: [
      "Mira el tablero desde arriba para ubicar filas y columnas.",
      "Distingue el primer piso grande, los pisos intermedios y el cuarto piso de G7.",
      "Separa anillo interior y anillo exterior cuando el piso lo permita.",
      "Ubica la banda como dos hileras juntas dentro de una misma cara.",
      "Relaciona anillos y bandas con rutas de ascenso o defensa.",
    ],
    examples: [
      "Una defensa puede depender del anillo interior porque controla vias de ascenso.",
      "Una banda ayuda a explicar por que dos casillas pertenecen a la misma cara del tablero.",
    ],
    faqs: [
      faq("Cuantos pisos tiene el tablero?", "El libro describe cuatro pisos, con G7 en el nivel mas alto."),
      faq("Anillo y banda son lo mismo?", "No. El anillo se relaciona con zonas internas o externas; la banda con hileras juntas de una cara."),
    ],
    links: [
      link("Tablero y casillas", "reglas/tablero-y-casillas"),
      link("Obstruccion", "reglas/obstruccion"),
      link("Valor del tablero", "cimax/valor-tablero-bloques"),
    ],
    cta: { label: "Ver tablero y casillas", path: "reglas/tablero-y-casillas" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "verified",
  },
  {
    slug: "tablero/esquinas-y-caras",
    category: "tableroLibro",
    title: "Esquinas y caras del tablero XIMA | Lectura avanzada",
    metaDescription:
      "Conoce como el libro organiza esquinas exteriores, interiores y caras del tablero XIMA para estudiar aperturas y rutas.",
    h1: "Esquinas y caras",
    intro:
      "El libro nombra esquinas exteriores, esquinas interiores y caras de la piramide para orientar aperturas y movimientos.",
    verified:
      "La base de tablero y coordenadas existe en Aprender. El libro aporta un vocabulario espacial mas detallado.",
    usage:
      "Esta lectura sirve para explicar aperturas, defensas y rutas simetricas sin repetir toda la regla del tablero.",
    caution:
      "Las esquinas y caras permiten comparar aperturas simetricas: una idea jugada por una cara puede tener equivalente por otra cara del tablero.",
    steps: [
      "Ubica las cuatro caras principales de la piramide.",
      "Identifica las esquinas exteriores del primer piso.",
      "Localiza las esquinas interiores descritas por el libro.",
      "Relaciona cada esquina con la cara desde la que se incorpora o defiende.",
      "Usa la simetria para comparar aperturas equivalentes.",
    ],
    examples: [
      "Una apertura por una cara puede tener una posicion equivalente en otra cara por simetria.",
      "Controlar esquinas puede influir en el acceso a segunda planta.",
    ],
    faqs: [
      faq("Esta lectura reemplaza coordenadas?", "No. Complementa coordenadas con una lectura espacial del libro."),
      faq("Sirve para principiantes?", "Sirve despues de conocer casillas, pisos y movimientos basicos."),
    ],
    links: [
      link("Coordenadas", "reglas/coordenadas"),
      link("Aperturas de XIMA", "estrategia/aperturas-xima"),
      link("Pisos y anillos", "tablero/pisos-anillos-bandas"),
    ],
    cta: { label: "Ver aperturas", path: "estrategia/aperturas-xima" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "partial",
  },
  {
    slug: "tablero/g7-y-simbolos-de-cima",
    category: "tableroLibro",
    title: "G7 y simbolos de la cima en XIMA | Lectura del libro",
    metaDescription:
      "Aprende como el libro identifica la cima de XIMA: G7, cuarto piso y simbolos usados para representar la victoria.",
    h1: "G7 y simbolos de la cima",
    intro:
      "El libro identifica la cima con G7 y menciona varias formas simbolicas de representar la llegada al punto final.",
    verified:
      "G7 es la casilla de la cima y el especial que llega legalmente cumple el objetivo.",
    usage:
      "G7 aparece en reglas, rescate, notacion y explicaciones del tablero porque concentra el objetivo de llegada.",
    caution:
      "Los simbolos del libro funcionan como vocabulario de estudio: ayudan a recordar que G7, cuarto piso y cima apuntan al mismo objetivo central.",
    steps: [
      "Ubica G7 en el tablero como la cima.",
      "Distingue G7 de las demas casillas del cuarto piso.",
      "Comprueba si el bloque que llega es especial o comun.",
      "Lee el simbolo de victoria solo dentro del contexto de la partida.",
      "Relaciona G7 con rescate cuando el bloque que llega es comun.",
    ],
    examples: [
      "G7 puede aparecer como referencia de cima en reglas y notacion.",
      "El comun que llega a G7 debe interpretarse como rescate si el especial fue capturado.",
    ],
    faqs: [
      faq("G7 es una casilla normal?", "No. Es la casilla objetivo de la cima."),
      faq("Los simbolos del libro aparecen en la interfaz?", "No necesariamente; se documentan como vocabulario del libro."),
    ],
    links: [
      link("La cima", "reglas/cima"),
      link("Bloque comun y G7", "reglas/bloque-comun-g7"),
      link("Sistema de notacion", "sistema-de-anotacion"),
    ],
    cta: { label: "Ver la cima", path: "reglas/cima" },
    sources: ["BOOK", "RULES", "KNOWLEDGE"],
    status: "verified",
  },
  {
    slug: "notacion/algebraica-muy-simplificada",
    category: "notacionLibro",
    title: "Notacion algebraica muy simplificada en XIMA | Libro oficial",
    metaDescription:
      "Aprende la notacion algebraica muy simplificada que describe el libro de XIMA y su relacion con coordenadas y replay.",
    h1: "Notacion algebraica muy simplificada",
    intro:
      "El libro presenta una notacion inspirada en el ajedrez, adaptada a XIMA por tener un solo tipo general de movimiento.",
    verified:
      "Aprender ya contiene sistema de anotacion y replay. El libro aporta la distincion entre notacion larga y corta.",
    usage:
      "La notacion larga usa casilla de salida y destino; la corta prioriza la casilla de llegada cuando no hay ambiguedad.",
    caution:
      "La notacion corta ahorra texto cuando el destino basta; la notacion larga conserva origen y destino cuando la jugada necesita mas claridad.",
    steps: [
      "Lee primero el numero de jugada.",
      "Identifica si la anotacion muestra salida y destino o solo destino.",
      "Busca signos que indiquen incorporacion o bloque especial.",
      "Si varias piezas pueden llegar a la misma casilla, revisa la desambiguacion.",
      "Contrasta la anotacion con el tablero o replay.",
    ],
    examples: [
      "La notacion larga puede indicar de donde sale y a donde llega el bloque.",
      "La notacion corta evita repetir salida cuando el destino basta para entender la jugada.",
    ],
    faqs: [
      faq("Como se relaciona con el replay?", "El replay ayuda a comprobar en el tablero lo que la notacion resume con origen, destino o signos."),
      faq("Por que es muy simplificada?", "Porque XIMA tiene un movimiento general para los bloques, no seis piezas diferentes como ajedrez."),
    ],
    links: [
      link("Sistema de anotacion", "sistema-de-anotacion"),
      link("Leer anotacion", "replay/leer-anotacion"),
      link("Signos de incorporacion", "notacion/signos-incorporacion-especial"),
    ],
    cta: { label: "Ver signos de notacion", path: "notacion/signos-incorporacion-especial" },
    sources: ["BOOK", "KNOWLEDGE"],
    status: "partial",
  },
  {
    slug: "notacion/signos-incorporacion-especial",
    category: "notacionLibro",
    title: "Signos de incorporacion y especial en XIMA | Notacion",
    metaDescription:
      "Conoce los signos que el libro asocia a incorporacion y bloque especial en la notacion de XIMA.",
    h1: "Signos de incorporacion y especial",
    intro:
      "El libro indica que la notacion puede marcar incorporacion y bloque especial con signos antes del movimiento.",
    verified:
      "La seccion de notacion del libro respalda estos signos como parte de su sistema algebraico muy simplificado.",
    usage:
      "Sirve para leer si una jugada coloca un bloque nuevo en el tablero o si participa el bloque especial.",
    caution:
      "Los signos ayudan a distinguir una incorporacion de un movimiento interno y a reconocer cuando participa el bloque especial.",
    steps: [
      "Lee la jugada completa antes de interpretar el signo.",
      "Busca si aparece un signo antes de la coordenada.",
      "Relaciona el signo menos con incorporacion segun el libro.",
      "Relaciona el signo del especial con la participacion del bloque clave.",
      "Verifica la jugada en tablero para confirmar que el signo corresponde.",
    ],
    examples: [
      "Una incorporacion se puede diferenciar de un movimiento de bloque ya colocado.",
      "El especial puede necesitar marca propia para evitar confundirlo con un bloque comun.",
    ],
    faqs: [
      faq("Para que sirven los signos?", "Sirven para identificar incorporaciones, movimientos del especial y diferencias entre jugadas parecidas."),
      faq("Ayudan al replay?", "Si aparecen junto a una partida, ayudan a leer el historial de jugadas."),
    ],
    links: [
      link("Notacion algebraica", "notacion/algebraica-muy-simplificada"),
      link("Sistema de anotacion", "sistema-de-anotacion"),
      link("Incorporar un bloque", "reglas/incorporar-bloque"),
    ],
    cta: { label: "Ver notacion algebraica", path: "notacion/algebraica-muy-simplificada" },
    sources: ["BOOK", "KNOWLEDGE"],
    status: "partial",
  },
  {
    slug: "estrategia/finales-xima",
    category: "estrategiaLibro",
    title: "Finales en XIMA | Como llegar a G7",
    metaDescription:
      "Aprende los conceptos de finales en XIMA segun el libro: tercera planta, oposicion del especial y camino hacia G7.",
    h1: "Finales en XIMA",
    intro:
      "El libro recomienda estudiar los finales porque la llegada a G7 depende de rutas, apoyo y oposicion del especial.",
    verified:
      "El objetivo verificado es llegar a la cima con el especial. El libro desarrolla como esa meta se decide muchas veces en tercera planta.",
    usage:
      "Practica finales buscando que el especial llegue con apoyo, evitando que una sola pieza quede aislada frente a la oposicion rival.",
    caution:
      "El final exige calcular oposicion, apoyo y ritmo: un especial aislado puede tener menos fuerza que un especial acompanado por un bloque comun activo.",
    steps: [
      "Ubica los bloques que tienen acceso a la tercera planta.",
      "Identifica si entre ellos esta tu bloque especial.",
      "Revisa la oposicion del especial contrario.",
      "Busca apoyo de un bloque comun antes de forzar G7.",
      "Evita sacrificar material si no abre una ruta clara a la cima.",
    ],
    examples: [
      "Un final con dos bloques propios, uno especial, suele ofrecer mas maniobra que un especial aislado.",
      "Llegar primero a tercera planta puede decidir la partida si crea acceso real a G7.",
    ],
    faqs: [
      faq("Por que estudiar finales primero?", "El libro sostiene que el final puede estudiarse por si solo y explica la meta concreta de G7."),
      faq("El especial es siempre ofensivo?", "El libro lo presenta defensivo en apertura y medio juego, pero mas ofensivo en finales por oposicion."),
    ],
    links: [
      link("Oposicion del especial", "reglas/oposicion-especial"),
      link("Tiempo en finales", "estrategia/tiempo-en-finales"),
      link("Final dos contra uno", "estrategia/final-dos-contra-uno"),
    ],
    cta: { label: "Ver tiempo en finales", path: "estrategia/tiempo-en-finales" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/tiempo-en-finales",
    category: "estrategiaLibro",
    title: "Tiempo en los finales de XIMA | Material y tercera planta",
    metaDescription:
      "Aprende por que el libro da tanta importancia al tiempo en los finales de XIMA y cuando llegar primero puede decidir.",
    h1: "Tiempo en los finales",
    intro:
      "El libro afirma que el tiempo es clave en los finales porque a menudo se sacrifica todo para llegar primero a la cima.",
    verified:
      "La importancia de G7 esta verificada; la idea de sacrificar material por tiempo pertenece a la teoria del libro.",
    usage:
      "Estudia las decisiones donde una jugada rapida hacia tercera planta vale mas que conservar un bloque pasivo.",
    caution:
      "No todos los sacrificios son correctos. El libro habla de compensacion, no de entregar bloques sin razon.",
    steps: [
      "Cuenta cuantos turnos necesita tu especial para acercarse a G7.",
      "Cuenta cuantos turnos necesita el especial contrario.",
      "Evalua si un bloque comun puede ahorrar un turno creando apoyo.",
      "Compara la ganancia de tiempo con el material que entregas.",
      "Juega por tiempo solo si mantiene amenaza real sobre la cima.",
    ],
    examples: [
      "Subir primero a tercera planta puede valer mas que capturar un bloque inofensivo.",
      "Una entrega de material solo tiene sentido si acelera una amenaza de cima.",
    ],
    faqs: [
      faq("Tiempo significa reloj?", "Aqui significa tambien ritmo de turnos y velocidad para llegar a G7."),
      faq("Debo sacrificar siempre?", "No. El sacrificio debe estar justificado por una ventaja clara de ruta o iniciativa."),
    ],
    links: [
      link("Finales en XIMA", "estrategia/finales-xima"),
      link("Material por tiempo", "estrategia/material-por-tiempo"),
      link("Iniciativa", "estrategia/iniciativa"),
    ],
    cta: { label: "Ver material por tiempo", path: "estrategia/material-por-tiempo" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/final-dos-contra-uno",
    category: "estrategiaLibro",
    title: "Final dos contra uno en XIMA | Especial con apoyo",
    metaDescription:
      "Conoce el final dos contra uno que describe el libro de XIMA: especial, bloque comun y apoyo hacia la cima.",
    h1: "Final dos contra uno",
    intro:
      "El libro plantea un final ideal donde dos bloques propios, uno de ellos especial, coordinan contra un especial rival.",
    verified:
      "La regla de victoria por especial en G7 sostiene esta idea: el bloque comun no reemplaza al especial, sino que le crea apoyo y rutas.",
    usage:
      "Practica este final buscando alinear el bloque comun con el especial propio y encontrar el punto ciego de la oposicion rival.",
    caution:
      "La idea central es reducir la posicion a apoyo contra oposicion: dos bloques coordinados buscan superar la resistencia de un especial rival.",
    steps: [
      "Lleva el especial a una casilla activa de tercera planta.",
      "Mantiene un bloque comun cercano que pueda apoyar.",
      "Evita que el especial rival bloquee la ruta directa a G7.",
      "Busca el angulo donde el comun sostiene el ascenso.",
      "Solo fuerza la cima si el apoyo evita la captura inmediata.",
    ],
    examples: [
      "Un especial aislado puede quedar neutralizado por oposicion.",
      "Un bloque comun alineado puede convertir una amenaza de cima en plan real.",
    ],
    faqs: [
      faq("Dos bloques siempre ganan?", "El libro lo presenta como principio de final, pero cada posicion debe revisarse legalmente."),
      faq("El bloque comun puede ganar?", "No. Su funcion es apoyar o rescatar, no reemplazar al especial ganador."),
    ],
    links: [
      link("Finales en XIMA", "estrategia/finales-xima"),
      link("Oposicion del especial", "reglas/oposicion-especial"),
      link("Bloque comun y G7", "reglas/bloque-comun-g7"),
    ],
    cta: { label: "Ver oposicion", path: "reglas/oposicion-especial" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/maniobra-zil",
    category: "estrategiaLibro",
    title: "Maniobra Zil en XIMA | Concepto estrategico del libro",
    metaDescription:
      "Explora la Maniobra Zil descrita en el libro de XIMA y sus fases para acercarse a la cima con apoyo.",
    h1: "Maniobra Zil",
    intro:
      "La Maniobra Zil aparece en el libro como una secuencia de fases para coordinar bloques hacia la cima.",
    verified:
      "El libro la describe con fases de ubicacion, alineacion, escalera y evasion de bloqueo para acercar bloques hacia la cima.",
    usage:
      "Puede estudiarse como patron de entrenamiento avanzado: ubicar, semicoordinar, crear escalera e incorporar en la casilla adecuada.",
    caution:
      "La Maniobra Zil se entiende mejor como una secuencia de construccion: primero se crea apoyo, despues se busca la ruta que evita el bloqueo rival.",
    steps: [
      "Ubica un bloque propio en tercera planta.",
      "Alinea un bloque de segunda planta que pueda coordinar o semicoordinar.",
      "Crea una escalera desde primera planta.",
      "Prepara una incorporacion que evada el bloqueo rival.",
      "Finaliza solo si la estructura conserva acceso real a G7.",
    ],
    examples: [
      "La fase de escalera busca habilitar mas de una casilla de coordinacion.",
      "La fase final depende de conservar apoyo suficiente mientras se evita el bloqueo rival.",
    ],
    faqs: [
      faq("La Maniobra Zil es una regla?", "No. Es una maniobra estrategica descrita en el libro."),
      faq("Como se estudia la Maniobra Zil?", "Por fases: ubicacion, alineacion, escalera, evasion del bloqueo y acceso a la cima."),
    ],
    links: [
      link("Crear escalera", "estrategia/bloques-escalones"),
      link("Principio de armonia", "estrategia/principio-armonia"),
      link("Finales en XIMA", "estrategia/finales-xima"),
    ],
    cta: { label: "Ver bloques escalones", path: "estrategia/bloques-escalones" },
    sources: ["BOOK"],
    status: "partial",
  },
  {
    slug: "estrategia/material-por-tiempo",
    category: "estrategiaLibro",
    title: "Material por tiempo en XIMA | Sacrificio y ritmo",
    metaDescription:
      "Aprende la transformacion de material en tiempo que propone el libro de XIMA para ganar iniciativa y llegar a G7.",
    h1: "Material por tiempo",
    intro:
      "El libro explica que a veces se sacrifica material para ganar tiempo, iniciativa o acceso mas rapido a la cima.",
    verified:
      "Las reglas permiten captura, movimiento y llegada a G7. La transformacion de material en tiempo es una lectura estrategica del libro.",
    usage:
      "Evalua si entregar un bloque abre una ruta, elimina un defensor o acelera una amenaza real.",
    caution:
      "No todo sacrificio es bueno: el libro insiste en que los cambios deben estar justificados.",
    steps: [
      "Identifica que material podrias perder.",
      "Calcula que turno o ruta ganarias a cambio.",
      "Comprueba si la ganancia mejora el acceso del especial a G7.",
      "Evalua si el rival obtiene una amenaza mayor.",
      "Ejecuta el sacrificio solo si la compensacion es clara.",
    ],
    examples: [
      "Entregar un bloque puede ser valido si elimina un defensor clave.",
      "Capturar un bloque inofensivo al inicio puede perder tiempos utiles.",
    ],
    faqs: [
      faq("Material por tiempo es una formula?", "No. Es una idea estrategica del libro."),
      faq("Se relaciona con CIMAX?", "El libro conecta valor de bloques y fuerza de juego, pero aqui se explica como decision de partida."),
    ],
    links: [
      link("Cambios de bloques", "estrategia/cambios-de-bloques"),
      link("Tiempo en finales", "estrategia/tiempo-en-finales"),
      link("Valor de tablero y bloques", "cimax/valor-tablero-bloques"),
    ],
    cta: { label: "Ver cambios de bloques", path: "estrategia/cambios-de-bloques" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/cambios-de-bloques",
    category: "estrategiaLibro",
    title: "Cambios de bloques en XIMA | Cuando estan justificados",
    metaDescription:
      "Aprende cuando el libro considera justificado cambiar bloques en XIMA: material, tiempo, defensa, estructura y final.",
    h1: "Cambios de bloques",
    intro:
      "El libro advierte que los bloques no se cambian por no saber que hacer: cada cambio debe perseguir un objetivo.",
    verified:
      "Las reglas de captura y dominacion permiten cambios de material. El libro aporta criterios estrategicos para evaluarlos.",
    usage:
      "Un cambio puede imponer ventaja material, ganar tiempos, eliminar defensores, desviar atacantes o pasar a un final favorable.",
    caution:
      "Un cambio bien justificado deja una posicion mas clara: mejor material, mas tiempo, menos defensa rival o un final mas favorable.",
    steps: [
      "Identifica que bloque se cambiaria y que bloque rival desaparece.",
      "Pregunta si el cambio gana material o tiempo.",
      "Revisa si elimina un defensor importante del rival.",
      "Comprueba si mejora tu estructura o dana la del contrario.",
      "Acepta el cambio solo si el final resultante favorece tu ruta a G7.",
    ],
    examples: [
      "Cambiar un bloque para eliminar un defensor de G7 puede ser estrategico.",
      "Cambiar sin plan puede abrir rutas al especial contrario.",
    ],
    faqs: [
      faq("Todo cambio de bloques es captura?", "Los cambios suelen relacionarse con captura o dominacion, pero se analizan por su resultado posicional."),
      faq("Que objetivo debe tener un cambio?", "Ganar material, tiempo, estructura, seguridad o un final favorable."),
    ],
    links: [
      link("Capturas por tipo", "reglas/capturas-tipos"),
      link("Material por tiempo", "estrategia/material-por-tiempo"),
      link("Finales en XIMA", "estrategia/finales-xima"),
    ],
    cta: { label: "Ver capturas por tipo", path: "reglas/capturas-tipos" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/espacio",
    category: "estrategiaLibro",
    title: "Espacio en XIMA | Control de casillas y segunda planta",
    metaDescription:
      "Aprende el concepto de espacio en XIMA segun el libro: casillas dominadas, segunda planta y ventaja posicional.",
    h1: "Espacio en XIMA",
    intro:
      "El libro define espacio como la cantidad de casillas o posiciones que domina cada jugador y donde puede colocar bloques.",
    verified:
      "El tablero, los pisos y las reglas de movimiento respaldan esta lectura; la relacion exacta con ventaja es teoria del libro.",
    usage:
      "Evalua espacio observando cuantas casillas utiles controlas, especialmente en segunda planta, antes de avanzar a tercera.",
    caution:
      "No se debe presentar el espacio como garantia de victoria; es un indicador posicional.",
    steps: [
      "Cuenta las casillas utiles que tus bloques controlan.",
      "Observa si tienes presencia en segunda planta.",
      "Revisa si el rival puede expulsarte o bloquearte.",
      "Distingue espacio real de bloques colocados sin apoyo.",
      "Convierte espacio en rutas hacia tercera planta y G7.",
    ],
    examples: [
      "Controlar segunda planta puede aumentar opciones de ascenso.",
      "Tener muchos bloques en primera planta no siempre significa tener mas espacio util.",
    ],
    faqs: [
      faq("Espacio es lo mismo que material?", "No. Material cuenta bloques; espacio mira casillas y posiciones controladas."),
      faq("Por que segunda planta importa?", "El libro la relaciona con ventaja de espacio y acceso a pisos superiores."),
    ],
    links: [
      link("Pisos y anillos", "tablero/pisos-anillos-bandas"),
      link("Administracion de bloques", "estrategia/administracion-bloques"),
      link("Ventaja", "diccionario/ventaja"),
    ],
    cta: { label: "Ver administracion de bloques", path: "estrategia/administracion-bloques" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/administracion-bloques",
    category: "estrategiaLibro",
    title: "Administracion de bloques en XIMA | Apertura y recursos",
    metaDescription:
      "Aprende como administrar bloques en XIMA segun el libro: desarrollo, reservas, apoyo y uso prudente del especial.",
    h1: "Administracion de bloques",
    intro:
      "El libro recomienda administrar los bloques como recursos: no todo debe incorporarse sin plan ni guardarse sin funcion.",
    verified:
      "La regla de incorporacion existe y el libro agrega recomendaciones sobre cuantos bloques desarrollar y cuando reservar.",
    usage:
      "Usa la administracion para equilibrar presencia en tablero, apoyo al especial, reservas para el final y control de vias de ascenso.",
    caution:
      "Las cantidades recomendadas por el libro son estrategia, no obligacion legal de la partida.",
    steps: [
      "Define cuantos bloques necesitas para ocupar espacio inicial.",
      "Evita mover repetidamente el mismo bloque sin desarrollo general.",
      "Mantiene al menos un recurso para apoyar o rescatar segun la posicion.",
      "No incorpores el especial sin una casilla ventajosa.",
      "Revisa si tus bloques coordinan antes de iniciar ataque.",
    ],
    examples: [
      "Reservar un bloque puede ser util para una oportunidad de final.",
      "Incorporar demasiados bloques sin coordinacion puede dejar rutas debiles.",
    ],
    faqs: [
      faq("Debo incorporar siempre el especial temprano?", "El libro recomienda no hacerlo al principio salvo posicion ventajosa."),
      faq("Administrar es jugar defensivo?", "No. Es organizar recursos para que ataque y defensa tengan apoyo."),
    ],
    links: [
      link("Incorporar un bloque", "reglas/incorporar-bloque"),
      link("Bloque especial", "reglas/bloque-especial"),
      link("Principio de armonia", "estrategia/principio-armonia"),
    ],
    cta: { label: "Ver principio de armonia", path: "estrategia/principio-armonia" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/principio-armonia",
    category: "estrategiaLibro",
    title: "Principio de armonia en XIMA | Coordinar fuerzas",
    metaDescription:
      "Conoce el principio de armonia en XIMA: coordinar bloques, elegir objetivos correctos y ejecutar un plan.",
    h1: "Principio de armonia",
    intro:
      "El libro define armonia como coordinar las fuerzas en la ejecucion de un plan para ocupar posiciones ventajosas.",
    verified:
      "La coordinacion de bloques se apoya en reglas de movimiento, dominacion, apoyo y rutas hacia la cima.",
    usage:
      "Antes de atacar, revisa si tus bloques persiguen el mismo objetivo o si estan dispersos sin apoyo.",
    caution:
      "La armonia aparece cuando los bloques no solo estan juntos, sino coordinados hacia un objetivo correcto: ruta, defensa, captura o ascenso.",
    steps: [
      "Elige un objetivo concreto: ruta, defensa, captura o acceso a G7.",
      "Ubica que bloques pueden apoyar ese objetivo.",
      "Evita coordinar fuerzas sobre una casilla sin valor real.",
      "Comprueba si el especial queda protegido durante el plan.",
      "Ejecuta solo cuando ataque y apoyo trabajen juntos.",
    ],
    examples: [
      "Dos bloques alineados con el especial pueden crear una amenaza mas clara que tres bloques dispersos.",
      "Coordinar sobre un objetivo equivocado no genera ventaja real.",
    ],
    faqs: [
      faq("Armonia es igual a alineacion?", "No. La alineacion puede ser parte de la armonia, pero el principio incluye plan y objetivo."),
      faq("Sirve para defensa?", "Si. La coordinacion tambien protege rutas y piezas importantes."),
    ],
    links: [
      link("Planificacion", "diccionario/planificacion"),
      link("Administracion de bloques", "estrategia/administracion-bloques"),
      link("Maniobra Feedback", "estrategia/maniobra-feedback"),
    ],
    cta: { label: "Ver Maniobra Feedback", path: "estrategia/maniobra-feedback" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/bloques-escalones",
    category: "estrategiaLibro",
    title: "Bloques escalones en XIMA | Crear rutas de ascenso",
    metaDescription:
      "Aprende que son los bloques escalones en XIMA y como el libro los relaciona con ascenso, apoyo y rutas a pisos superiores.",
    h1: "Bloques escalones",
    intro:
      "El libro usa la idea de bloques escalones para explicar estructuras que ayudan a subir a pisos superiores.",
    verified:
      "Las reglas permiten subir sobre bloques cuando se cumplen condiciones de altura. El concepto de escalon organiza esa posibilidad.",
    usage:
      "Un escalon debe abrir una ruta; si queda condenado a no ascender, el libro recomienda no abusar de esa funcion.",
    caution:
      "Crear escalera es teoria posicional; cada ejemplo debe respetar altura, linea y respuesta rival.",
    steps: [
      "Elige una casilla que pueda servir de apoyo de ascenso.",
      "Coloca o mueve un bloque sin dejarlo aislado.",
      "Comprueba si otro bloque puede coordinar con ese escalon.",
      "Evita designar demasiados bloques a la misma funcion pasiva.",
      "Transforma la escalera en acceso real al piso superior.",
    ],
    examples: [
      "Un bloque escalon puede ayudar a subir a segunda o tercera planta.",
      "Demasiados escalones pasivos reducen material activo para ataque o defensa.",
    ],
    faqs: [
      faq("Escalon es una pieza nueva?", "No. Es un uso estrategico de un bloque comun o especial."),
      faq("Puede un escalon capturar?", "Depende de la posicion; su funcion principal aqui es apoyar el ascenso."),
    ],
    links: [
      link("Subir bloques", "reglas/subir-bloques"),
      link("Maniobra Feedback", "estrategia/maniobra-feedback"),
      link("Maniobra Zil", "estrategia/maniobra-zil"),
    ],
    cta: { label: "Ver subir bloques", path: "reglas/subir-bloques" },
    sources: ["BOOK", "RULES", "UNITY"],
    status: "partial",
  },
  {
    slug: "estrategia/aperturas-xima",
    category: "estrategiaLibro",
    title: "Aperturas en XIMA | Primeras jugadas segun el libro",
    metaDescription:
      "Explora como el libro presenta las aperturas en XIMA, la simetria del tablero y el objetivo de ocupar segunda planta.",
    h1: "Aperturas en XIMA",
    intro:
      "El libro explica que la apertura condiciona el desarrollo de la partida y que la simetria del tablero reduce posiciones equivalentes.",
    verified:
      "La incorporacion inicial y el tablero 3D sostienen las aperturas: el libro organiza las primeras jugadas segun simetria, caras y control de plantas.",
    usage:
      "Estudia aperturas buscando desarrollo rapido, control de segunda planta, esquinas utiles y coordinacion de bloques.",
    caution:
      "El objetivo de la apertura es desarrollar sin perder tiempos: ocupar casillas utiles, controlar esquinas y preparar el ascenso antes de exponer el especial.",
    steps: [
      "Elige una cara de incorporacion y reconoce su simetria.",
      "Incorpora un bloque que ayude al control de segunda planta.",
      "Evita perseguir capturas inofensivas al inicio.",
      "Coordina bloques antes de iniciar ataque directo.",
      "Revisa si el especial debe esperar una posicion mas segura.",
    ],
    examples: [
      "Una apertura puede priorizar esquina o anillo interior segun el plan.",
      "El libro advierte que capturar temprano sin objetivo puede perder tiempos.",
    ],
    faqs: [
      faq("Las aperturas son obligatorias?", "No. Son patrones de estudio del libro."),
      faq("Por que el libro habla de simetria?", "Porque las cuatro caras del tablero permiten estudiar aperturas equivalentes desde zonas distintas."),
    ],
    links: [
      link("Defensas de XIMA", "estrategia/defensas-xima"),
      link("Esquinas y caras", "tablero/esquinas-y-caras"),
      link("Administracion de bloques", "estrategia/administracion-bloques"),
    ],
    cta: { label: "Ver defensas", path: "estrategia/defensas-xima" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/defensas-xima",
    category: "estrategiaLibro",
    title: "Defensas en XIMA | Bajas, medias, altas e internas",
    metaDescription:
      "Aprende como el libro clasifica defensas en XIMA: bajas, medias, altas, internas, externas, directas e indirectas.",
    h1: "Defensas en XIMA",
    intro:
      "El libro define las defensas como respuestas que previenen el ataque de una apertura y controlan vias de ascenso.",
    verified:
      "La defensa se apoya en movimiento, altura y dominacion. El libro la clasifica por altura, anillo y relacion con la apertura rival.",
    usage:
      "Clasifica una defensa por altura, anillo y relacion con la linea de ataque de la apertura rival.",
    caution:
      "Una defensa fuerte no solo detiene una amenaza: tambien conserva vias propias de ascenso y evita que el rival gane la segunda planta con facilidad.",
    steps: [
      "Identifica la apertura o amenaza del rival.",
      "Decide si responder en primera, segunda o tercera linea de combate.",
      "Distingue defensa interna o externa segun el anillo.",
      "Comprueba si la defensa cruza directamente la linea de ataque.",
      "Elige una defensa que tambien conserve plan de ascenso propio.",
    ],
    examples: [
      "Una defensa baja puede frenar apoyo rival en primera planta.",
      "Una defensa indirecta puede no tocar la amenaza de inmediato, pero limita una ruta futura.",
    ],
    faqs: [
      faq("Defensa es solo evitar perder?", "No. En XIMA defender tambien puede preparar ascenso y control de vias."),
      faq("Las defensas tienen tipos?", "Si. El libro las organiza como bajas, medias, altas, internas, externas, directas e indirectas."),
    ],
    links: [
      link("Aperturas de XIMA", "estrategia/aperturas-xima"),
      link("Bloqueo y obstruccion", "reglas/bloqueo-vs-obstruccion"),
      link("Defensa", "diccionario/defensa"),
    ],
    cta: { label: "Ver aperturas", path: "estrategia/aperturas-xima" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "estrategia/maniobra-feedback",
    category: "estrategiaLibro",
    title: "Maniobra Feedback en XIMA | Cinco fases del libro",
    metaDescription:
      "Conoce la Maniobra Feedback descrita en el libro de XIMA: planificacion, inversion, creacion, lanzamiento y recuperacion.",
    h1: "Maniobra Feedback",
    intro:
      "La Maniobra Feedback busca llevar un bloque primero que el contrario a un piso superior mediante alineacion y escalera.",
    verified:
      "El libro la describe como maniobra presente en apertura, medio juego y final, orientada a subir primero a un piso superior.",
    usage:
      "Estudiala como patron de entrenamiento: construir base, doblar columna, crear escalera, lanzar y recuperar ventaja.",
    caution:
      "La clave del Feedback es invertir un tiempo para crear una estructura que luego devuelve ventaja: base, escalera, lanzamiento y recuperacion.",
    steps: [
      "Planifica el lugar donde se desarrollara la estructura.",
      "Invierte un tiempo en doblar columna o crear base.",
      "Crea el bloque escalera que sostendra el ascenso.",
      "Lanza el bloque hacia la casilla interior del piso superior.",
      "Recupera la ventaja obtenida sin dejar el especial vulnerable.",
    ],
    examples: [
      "La fase de inversion puede parecer lenta, pero prepara la subida posterior.",
      "La fase de lanzamiento es peligrosa si el rival puede capturar o bloquear.",
    ],
    faqs: [
      faq("Feedback es una regla?", "No. Es una maniobra estrategica del libro."),
      faq("Sirve solo en apertura?", "El libro dice que aparece en apertura, medio juego y final, aunque se usa mucho en apertura."),
    ],
    links: [
      link("Bloques escalones", "estrategia/bloques-escalones"),
      link("Principio de armonia", "estrategia/principio-armonia"),
      link("Aperturas de XIMA", "estrategia/aperturas-xima"),
    ],
    cta: { label: "Ver bloques escalones", path: "estrategia/bloques-escalones" },
    sources: ["BOOK"],
    status: "partial",
  },
  {
    slug: "estrategia/iniciativa",
    category: "estrategiaLibro",
    title: "Iniciativa en XIMA | Ritmo, amenaza y ascenso",
    metaDescription:
      "Aprende que significa iniciativa en XIMA segun el libro: marcar el ritmo con amenazas de ascenso hacia G7.",
    h1: "Iniciativa en XIMA",
    intro:
      "El libro define iniciativa como marcar el ritmo de las acciones y atraer la atencion rival con un plan constante de ascenso.",
    verified:
      "La iniciativa se apoya en reglas reales de movimiento, amenaza, dominacion y objetivo de G7.",
    usage:
      "Busca iniciativa cuando tus jugadas obligan al rival a responder mientras conservas ruta hacia la cima.",
    caution:
      "No confundas iniciativa con mover rapido sin plan; la amenaza debe ser concreta.",
    steps: [
      "Identifica una amenaza real de ascenso o captura.",
      "Comprueba que el rival tenga que responder.",
      "Transforma una amenaza simple en doble si la posicion lo permite.",
      "Mantiene protegido el especial mientras presionas.",
      "Conserva la iniciativa solo si cada jugada mejora tu ruta a G7.",
    ],
    examples: [
      "Amenazar el especial rival puede ganar tiempo si tambien mantiene tu ascenso.",
      "Una amenaza sobre G7 obliga a revisar defensa inmediata.",
    ],
    faqs: [
      faq("Iniciativa es atacar siempre?", "No. Es mantener el ritmo y obligar respuestas utiles para tu plan."),
      faq("Puede perderse la iniciativa?", "Si. Una jugada sin amenaza o sin apoyo puede entregarla al rival."),
    ],
    links: [
      link("Ataque", "diccionario/ataque"),
      link("Amenazas al especial", "reglas/amenazas-al-especial"),
      link("Tiempo en finales", "estrategia/tiempo-en-finales"),
    ],
    cta: { label: "Ver amenazas al especial", path: "reglas/amenazas-al-especial" },
    sources: ["BOOK", "RULES"],
    status: "partial",
  },
  {
    slug: "cimax/valor-tablero-bloques",
    category: "cimaxLibro",
    title: "Valor del tablero y los bloques en XIMA | Libro y CIMAX",
    metaDescription:
      "Aprende como el libro valora tablero, bloques fuera, bloques dominados y posiciones para estudiar fuerza de juego.",
    h1: "Valor del tablero y los bloques",
    intro:
      "El libro afirma que en XIMA todo tiene valor: bloques, tablero, posiciones, dominacion y funciones durante la partida.",
    verified:
      "El producto contiene CIMAX y estadisticas. El libro ofrece una teoria de valor para entender fuerza de juego.",
    usage:
      "Un bloque no vale solo por existir: su importancia depende de posicion, piso, dominacion y funcion.",
    caution:
      "El valor de un bloque cambia por su funcion: estar fuera, ocupar un piso, dominar, quedar dominado, apoyar una ruta o participar en captura.",
    steps: [
      "Identifica si el bloque esta fuera, sobre el tablero, dominando o dominado.",
      "Ubica el piso o posicion que ocupa.",
      "Revisa si cumple una funcion: apoyo, bloqueo, captura, rescate o amenaza.",
      "Separa valor absoluto de valor real en la posicion.",
      "Relaciona el valor con la funcion del bloque antes de comparar la posicion.",
    ],
    examples: [
      "Un bloque dominado puede perder movilidad aunque siga presente.",
      "Un bloque que domina una columna puede aumentar su valor funcional.",
    ],
    faqs: [
      faq("Esto calcula CIMAX exacto?", "No. Explica como el libro piensa el valor de tablero y bloques para estudiar una partida."),
      faq("Por que importa el tablero?", "Porque el libro asigna importancia a posicion, piso y cercania a la cima."),
    ],
    links: [
      link("CIMAX segun el libro", "cimax/segun-el-libro"),
      link("Dominacion avanzada", "reglas/dominacion-tipos"),
      link("Interpretar CIMAX", "estadisticas/interpretar-cimax"),
    ],
    cta: { label: "Ver CIMAX segun el libro", path: "cimax/segun-el-libro" },
    sources: ["BOOK", "CIMAX", "RULES"],
    status: "partial",
  },
  {
    slug: "cimax/segun-el-libro",
    category: "cimaxLibro",
    title: "CIMAX segun el libro | Fuerza, nivel y potencia",
    metaDescription:
      "Explicacion del CIMAX como lo presenta el libro Como llegar a la Xima, contrastada con las metricas visibles del proyecto.",
    h1: "CIMAX segun el libro",
    intro:
      "El libro presenta CIMAX como un metodo matematico para medir nivel y habilidad relativa usando fuerza, potencia, tiempo y turnos.",
    verified:
      "El backend actual contiene calculos de transferencia CIMAX, potencia, nivel de juego y puntajes por modo. El libro aporta el marco teorico original.",
    usage:
      "La teoria del libro se conecta con lo que el jugador ve en estadisticas: CIMAX por modo, progreso y comparacion de rendimiento.",
    caution:
      "La lectura del libro ayuda a entender por que CIMAX no mira solo ganar o perder: tambien considera fuerza, potencia, tiempo y turnos.",
    steps: [
      "Lee CIMAX como una metrica de progreso dentro de XIMA.",
      "Distingue fuerza de juego, nivel de juego y potencia.",
      "Relaciona potencia con fuerza, tiempo efectivo y turnos empleados.",
      "Compara el concepto del libro con los paneles visibles de estadisticas.",
      "Usa el resultado visible de XIMA como referencia final de progreso.",
    ],
    examples: [
      "El libro relaciona potencia con jugar fuerte usando menos tiempo y menos turnos.",
      "El backend visible trabaja con nivel de juego, ratio de potencia y transferencia entre jugadores.",
    ],
    faqs: [
      faq("Que aporta el libro al CIMAX?", "Aporta el marco conceptual: fuerza de juego, nivel, potencia, tiempo efectivo, turnos y valor de los bloques."),
      faq("CIMAX aparece en el producto?", "Si. Estadisticas y ranking muestran CIMAX por modo y el backend calcula transferencias."),
    ],
    links: [
      link("Fuerza, nivel y potencia", "cimax/fuerza-nivel-potencia"),
      link("Valor de tablero y bloques", "cimax/valor-tablero-bloques"),
      link("CIMAX", "estadisticas/cimax"),
    ],
    cta: { label: "Ver fuerza y potencia", path: "cimax/fuerza-nivel-potencia" },
    sources: ["BOOK", "CIMAX"],
    status: "partial",
  },
  {
    slug: "cimax/fuerza-nivel-potencia",
    category: "cimaxLibro",
    title: "Fuerza, nivel y potencia en CIMAX | Guia del libro",
    metaDescription:
      "Aprende los conceptos de fuerza de juego, nivel de juego, potencia, tiempo efectivo y turnos dentro del CIMAX de XIMA.",
    h1: "Fuerza, nivel y potencia",
    intro:
      "El libro organiza CIMAX alrededor de fuerza de juego, nivel de juego y potencia para valorar la calidad del resultado.",
    verified:
      "El backend tambien usa fuerza, nivel de juego, potencia, tiempo usado y turnos, lo que conecta la teoria del libro con datos de partida.",
    usage:
      "Fuerza resume valores del juego; nivel combina fuerzas de jugadores; potencia relaciona fuerza con tiempo efectivo y turnos.",
    caution:
      "Fuerza, nivel y potencia se leen mejor juntos: la fuerza mide lo conseguido en la partida, el nivel combina a los jugadores y la potencia relaciona resultado con tiempo y turnos.",
    steps: [
      "Calcula primero la fuerza como lectura de la posicion o partida.",
      "Suma las fuerzas para entender el nivel de juego.",
      "Relaciona la fuerza de cada jugador con su tiempo efectivo.",
      "Incluye los turnos empleados para interpretar potencia.",
      "Compara el resultado con el panel de estadisticas para estudiar tu progreso por modo.",
    ],
    examples: [
      "Una partida con alta fuerza y pocos turnos puede mostrar mayor potencia.",
      "Dos jugadores pueden tener fuerzas distintas dentro de un mismo nivel de juego.",
    ],
    faqs: [
      faq("Potencia es lo mismo que CIMAX?", "No. Potencia es un componente o lectura dentro del marco de CIMAX."),
      faq("Puedo calcularlo manualmente?", "Puedes estudiar la idea general, pero el valor que cuenta en XIMA es el que aparece en el sistema."),
    ],
    links: [
      link("CIMAX segun el libro", "cimax/segun-el-libro"),
      link("Interpretar CIMAX", "estadisticas/interpretar-cimax"),
      link("Estadisticas", "estadisticas"),
    ],
    cta: { label: "Ver interpretar CIMAX", path: "estadisticas/interpretar-cimax" },
    sources: ["BOOK", "CIMAX"],
    status: "partial",
  },
];

const publicBookLearnTopics = BOOK_LEARN_TOPICS.filter(hasMinimumPublicInformation);
const PUBLIC_BOOK_LEARN_SLUGS = new Set(publicBookLearnTopics.map((topic) => topic.slug));

export const BOOK_LEARN_NAVIGATION_GROUPS = [
  {
    label: "Libro: reglas avanzadas",
    slugs: [
      "reglas/ahogado",
      "reglas/sin-recurso",
      "reglas/posicion-muerta",
      "reglas/incremento-material-avanzado",
      "reglas/dominacion-tipos",
      "reglas/capturas-tipos",
      "reglas/oposicion-especial",
      "reglas/amenazas-al-especial",
      "reglas/bloqueo-superior-inferior",
      "reglas/obstruccion",
      "reglas/bloqueo-vs-obstruccion",
      "reglas/rescate-avanzado",
      "reglas/bloque-comun-g7",
    ],
  },
  {
    label: "Libro: tablero y notacion",
    slugs: [
      "tablero/pisos-anillos-bandas",
      "tablero/esquinas-y-caras",
      "tablero/g7-y-simbolos-de-cima",
      "notacion/algebraica-muy-simplificada",
      "notacion/signos-incorporacion-especial",
    ],
  },
  {
    label: "Libro: estrategia y finales",
    slugs: [
      "estrategia/finales-xima",
      "estrategia/tiempo-en-finales",
      "estrategia/final-dos-contra-uno",
      "estrategia/maniobra-zil",
      "estrategia/material-por-tiempo",
      "estrategia/cambios-de-bloques",
      "estrategia/espacio",
      "estrategia/administracion-bloques",
      "estrategia/principio-armonia",
      "estrategia/bloques-escalones",
      "estrategia/aperturas-xima",
      "estrategia/defensas-xima",
      "estrategia/maniobra-feedback",
      "estrategia/iniciativa",
    ],
  },
  {
    label: "Libro: CIMAX y valores",
    slugs: [
      "cimax/valor-tablero-bloques",
      "cimax/segun-el-libro",
      "cimax/fuerza-nivel-potencia",
    ],
  },
].map((group) => ({
  ...group,
  slugs: group.slugs.filter((slug) => PUBLIC_BOOK_LEARN_SLUGS.has(slug)),
})).filter((group) => group.slugs.length > 0);

export const BOOK_LEARN_SLUGS = publicBookLearnTopics.map((topic) => topic.slug);

export const buildBookLearnPages = (helpers) =>
  publicBookLearnTopics.map((topic) => buildLearnPage(helpers, topic));
